import { op, Operation } from "libs/transaction";
import { createAction } from "./action";
import { InvalidActionStateError } from "./utils/errors";
import * as ops from "libs/operations";
import { generateRecordId, getPointer, isGroupTagRecord, isTagPrivate, ThreadVisibility } from "libs/schema";
import { UnreachableCaseError } from "libs/errors";
import { authorizeUserHasAccessToThread } from "./authorizers/threadAuthorizers";
import { authorizeUserHasAccessToTags } from "./authorizers/tagAuthorizers";
import { authorizeUserHasAccessToMessage } from "./authorizers/messageAuthorizers";

/* -------------------------------------------------------------------------------------------------
 *  updateThreadSubject
 * -------------------------------------------------------------------------------------------------
 */

type UpdateThreadSubjectProps = {
  threadId: string;
  subject: string;
};

export const updateThreadSubject = createAction<UpdateThreadSubjectProps>("thread.updateThreadSubject", {
  operations: async (props, context) => {
    const { threadId, subject } = props;

    const [thread] = await context.environment.loader.getRecord("thread", threadId);

    if (!thread) {
      throw new InvalidActionStateError("[updateThreadSubject] Thread not found", {
        errorData: { id: threadId },
      });
    }

    return [op.update(getPointer("thread", threadId), { subject })];
  },
  authorize: async (props, context) => {
    await authorizeUserHasAccessToThread({ threadId: props.threadId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  addGroupToThread
 * -------------------------------------------------------------------------------------------------
 */

type AddGroupToThreadProps = {
  threadId: string;
  groupId: string;
};

export const addGroupToThread = createAction<AddGroupToThreadProps>("thread.addGroupToThread", {
  operations: async (props, context) => {
    const { loader } = context.environment;

    const [[thread], [group], [userProfile]] = await Promise.all([
      loader.getRecord("thread", props.threadId),
      loader.getRecord("tag", props.groupId),
      loader.getRecord("user_profile", context.currentUserId),
    ]);

    if (!thread) {
      throw new InvalidActionStateError("[addGroupToThread] Thread not found", { errorData: { props } });
    } else if (!group) {
      throw new InvalidActionStateError("[addGroupToThread] Group not found", { errorData: { props } });
    } else if (!userProfile) {
      throw new InvalidActionStateError("[addGroupToThread] User profile not found", {
        errorData: { props, currentUserId: context.currentUserId },
      });
    } else if (thread.visibility === "PRIVATE" ? !isTagPrivate(group) : isTagPrivate(group)) {
      const message = `Cannot add ${
        thread.visibility === "PRIVATE" ? "shared" : "private"
      } to ${thread.visibility.toLowerCase()} thread.`;

      throw new InvalidActionStateError(`[addGroupToThread] ${message}`, {
        userMessage: message,
        errorData: { props },
      });
    }

    const pointer = getPointer("thread_group_permission", {
      thread_id: props.threadId,
      group_id: props.groupId,
    });

    return op.upsert(pointer, {
      onCreate: [
        op.set(pointer, {
          id: pointer.id,
          thread_id: props.threadId,
          group_id: props.groupId,
          creator_user_id: context.currentUserId,
          owner_organization_id: userProfile.owner_organization_id,
          start_at: thread.first_message_timeline_order,
          thread_sent_at: thread.first_message_sent_at,
        }),
      ],
    });
  },
  authorize: async (props, context) => {
    await Promise.all([
      authorizeUserHasAccessToThread({ threadId: props.threadId, context }),
      authorizeUserHasAccessToTags({ tagIds: [props.groupId], context }),
    ]);
  },
});

/* -------------------------------------------------------------------------------------------------
 *  removeGroupFromThread
 * -------------------------------------------------------------------------------------------------
 */

type RemoveGroupFromThreadProps = {
  threadId: string;
  groupId: string;
};

export const removeGroupFromThread = createAction<RemoveGroupFromThreadProps>("thread.removeGroupFromThread", {
  operations: async (props, context) => {
    const { loader } = context.environment;

    const [[thread], [group]] = await Promise.all([
      loader.getRecord("thread", props.threadId),
      loader.getRecord("tag", props.groupId),
    ]);

    if (!thread) {
      throw new InvalidActionStateError("[removeGroupFromThread] Thread not found", { errorData: { props } });
    } else if (!group || !isGroupTagRecord(group)) {
      const message = "Group not found. This could be because you don't have permission to access this group.";

      throw new InvalidActionStateError(`[removeGroupFromThread] ${message}`, {
        userMessage: message,
        errorData: { props },
      });
    } else if (group.data?.is_organization_group) {
      const message = "Cannot manually remove an organization group. Instead, change the thread visibility.";

      throw new InvalidActionStateError(`[removeGroupFromThread] ${message}`, {
        userMessage: message,
        errorData: { props },
      });
    }

    const pointer = getPointer("thread_group_permission", {
      thread_id: props.threadId,
      group_id: props.groupId,
    });

    return op.delete(pointer.table, pointer);
  },
  authorize: async (props, context) => {
    await Promise.all([
      authorizeUserHasAccessToThread({ threadId: props.threadId, context }),
      authorizeUserHasAccessToTags({ tagIds: [props.groupId], context }),
    ]);
  },
});

/* -------------------------------------------------------------------------------------------------
 *  applyLabelToThread
 * -------------------------------------------------------------------------------------------------
 */

type ApplyLabelToThreadProps = {
  threadId: string;
  labelId: string;
};

export const applyLabelToThread = createAction<ApplyLabelToThreadProps>("thread.applyLabelToThread", {
  operations: async (props, context) => {
    const { loader } = context.environment;

    const [[thread], [label], [userProfile]] = await Promise.all([
      loader.getRecord("thread", props.threadId),
      loader.getRecord("tag", props.labelId),
      loader.getRecord("user_profile", context.currentUserId),
    ]);

    if (!thread) {
      throw new InvalidActionStateError("[applyLabelToThread] Thread not found", { errorData: { props } });
    }

    if (!label) {
      throw new InvalidActionStateError("[applyLabelToThread] Label not found", { errorData: { props } });
    }

    if (!userProfile) {
      throw new InvalidActionStateError("[applyLabelToThread] User profile not found", {
        errorData: { props, currentUserId: context.currentUserId },
      });
    }

    const pointer = getPointer("thread_tag", {
      thread_id: props.threadId,
      tag_id: props.labelId,
    });

    return op.upsert(pointer, {
      onCreate: [
        op.set(pointer, {
          id: pointer.id,
          thread_id: props.threadId,
          tag_id: props.labelId,
          tag_type: label.type,
          data: null,
          creator_user_id: context.currentUserId,
          owner_organization_id: userProfile.owner_organization_id,
        }),
      ],
    });
  },
  authorize: async (props, context) => {
    await Promise.all([
      authorizeUserHasAccessToThread({ threadId: props.threadId, context }),
      authorizeUserHasAccessToTags({ tagIds: [props.labelId], context }),
    ]);
  },
});

/* -------------------------------------------------------------------------------------------------
 *  removeLabelFromThread
 * -------------------------------------------------------------------------------------------------
 */

type RemoveLabelFromThreadProps = {
  threadId: string;
  labelId: string;
};

export const removeLabelFromThread = createAction<RemoveLabelFromThreadProps>("thread.removeLabelFromThread", {
  operations: async (props, context) => {
    const { loader } = context.environment;

    const [[thread], [label]] = await Promise.all([
      loader.getRecord("thread", props.threadId),
      loader.getRecord("tag", props.labelId),
    ]);

    if (!thread) {
      throw new InvalidActionStateError("[removeLabelFromThread] Thread not found", { errorData: { props } });
    }

    if (!label) {
      throw new InvalidActionStateError("[removeLabelFromThread] Label not found", { errorData: { props } });
    }

    const pointer = getPointer("thread_tag", {
      thread_id: props.threadId,
      tag_id: props.labelId,
    });

    return op.delete(pointer);
  },
  authorize: async (props, context) => {
    await Promise.all([
      authorizeUserHasAccessToThread({ threadId: props.threadId, context }),
      authorizeUserHasAccessToTags({ tagIds: [props.labelId], context }),
    ]);
  },
});

/* -------------------------------------------------------------------------------------------------
 *  updateThreadVisibility
 * -------------------------------------------------------------------------------------------------
 */

type UpdateThreadVisibilityProps = {
  threadId: string;
  visibility: ThreadVisibility;
};

export const updateThreadVisibility = createAction<UpdateThreadVisibilityProps>("thread.updateThreadVisibility", {
  operations: async (props, context) => {
    const { loader } = context.environment;

    const operations: Operation[] = [];

    const [[thread], [groupPermissions], [userProfile]] = await Promise.all([
      loader.getRecord("thread", props.threadId),
      loader.getThreadGroupPermissions({
        thread_id: props.threadId,
      }),
      loader.getRecord("user_profile", context.currentUserId),
    ]);

    if (!thread) {
      throw new InvalidActionStateError("[updateThreadVisibility] Thread not found", { errorData: { props } });
    }

    if (!userProfile) {
      throw new InvalidActionStateError("[updateThreadVisibility] User profile not found", {
        errorData: { props, currentUserId: context.currentUserId },
      });
    }

    const currentUserPermissionPointer = getPointer("thread_user_permission", {
      thread_id: props.threadId,
      user_id: context.currentUserId,
    });

    // On visibility changes we make sure the current user has permission to access the thread
    // to prevent them from losing access to the thread after the visibility change.
    const permissionForCurrentUserUpsert = op.upsert(currentUserPermissionPointer, {
      onCreate: [
        op.set("thread_user_permission", {
          id: currentUserPermissionPointer.id,
          thread_id: props.threadId,
          user_id: context.currentUserId,
          creator_user_id: context.currentUserId,
          owner_organization_id: userProfile.owner_organization_id,
          start_at: thread.first_message_timeline_order,
          thread_sent_at: thread.first_message_sent_at,
        }),
      ],
    });

    const { visibility } = props;

    switch (visibility) {
      case "PRIVATE": {
        operations.push(
          op.update(getPointer("thread", props.threadId), { visibility }),
          permissionForCurrentUserUpsert,
          ...groupPermissions.map((permission) => {
            return op.delete("thread_group_permission", permission);
          }),
        );

        break;
      }
      case "SHARED": {
        operations.push(
          op.update(getPointer("thread", props.threadId), { visibility }),
          ...groupPermissions.map((permission) => {
            return op.delete("thread_group_permission", permission);
          }),
          permissionForCurrentUserUpsert,
          op.set("thread_group_permission", {
            id: generateRecordId("thread_group_permission", {
              thread_id: props.threadId,
              group_id: userProfile.owner_organization_id,
            }),
            thread_id: props.threadId,
            group_id: userProfile.owner_organization_id,
            creator_user_id: context.currentUserId,
            owner_organization_id: userProfile.owner_organization_id,
            start_at: thread.first_message_timeline_order,
            thread_sent_at: thread.first_message_sent_at,
          }),
        );

        break;
      }
      default: {
        throw new UnreachableCaseError(visibility);
      }
    }

    return operations;
  },
  authorize: async (props, context) => {
    await authorizeUserHasAccessToThread({ threadId: props.threadId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  markThreadSeen
 * -------------------------------------------------------------------------------------------------
 */

type MarkThreadSeenProps = {
  threadId: string;
  seenToTimelineId: string;
  seenToTimelineOrder: string;
  ownerOrganizationId: string;
};

export const markThreadSeen = createAction<MarkThreadSeenProps>("thread.markThreadSeen", {
  operations: async (props, context) => {
    return op.set("thread_seen_receipt", {
      id: generateRecordId("thread_seen_receipt", {
        thread_id: props.threadId,
        user_id: context.currentUserId,
      }),
      thread_id: props.threadId,
      user_id: context.currentUserId,
      seen_to_timeline_id: props.seenToTimelineId,
      seen_to_timeline_order: props.seenToTimelineOrder,
      owner_organization_id: props.ownerOrganizationId,
    });
  },
  authorize: async () => {
    // This authorizer was intentionally not updated because the change is low-stakes and occurs frequently enough to
    // warrant performance optimization.
  },
});

/* -------------------------------------------------------------------------------------------------
 *  markThreadResolved
 * -------------------------------------------------------------------------------------------------
 */

type MarkThreadResolvedProps = {
  threadId: string;
  resolvedByMessageId: string;
  ownerOrganizationId: string;
};

export const markThreadResolved = createAction<MarkThreadResolvedProps>("thread.markThreadResolved", {
  operations: async (props, context) => {
    const { loader } = context.environment;

    const [message] = await loader.getRecord("message", props.resolvedByMessageId);

    if (!message) {
      throw new InvalidActionStateError("[markThreadResolved] Message not found", { errorData: { props } });
    }

    if (message.sent_at > new Date().toISOString()) {
      const message = "The message that resolves the thread has not been sent yet.";

      throw new InvalidActionStateError(`[markThreadResolved] ${message}`, {
        userMessage: message,
        errorData: { props },
      });
    }

    return ops.thread.markThreadResolved({
      threadId: props.threadId,
      resolvedByMessageId: props.resolvedByMessageId,
      markedResolvedByUserId: context.currentUserId,
      ownerOrganizationId: props.ownerOrganizationId,
    });
  },
  authorize: async (props, context) => {
    await authorizeUserHasAccessToMessage({
      messageId: props.resolvedByMessageId,
      context,
    });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  markThreadNotResolved
 * -------------------------------------------------------------------------------------------------
 */

type MarkThreadNotResolvedProps = {
  threadId: string;
};

export const markThreadNotResolved = createAction<MarkThreadNotResolvedProps>("thread.markThreadNotResolved", {
  operations: async (props) => {
    return ops.thread.markThreadNotResolved({
      threadId: props.threadId,
    });
  },
  authorize: async (props, context) => {
    await authorizeUserHasAccessToThread({ threadId: props.threadId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  deleteThread
 * -------------------------------------------------------------------------------------------------
 */

type DeleteThreadProps = {
  threadId: string;
};

export const deleteThread = createAction<DeleteThreadProps>("thread.deleteThread", {
  operations: async (props) => {
    return ops.thread.deleteThread({
      threadId: props.threadId,
    });
  },
  authorize: async (props, context) => {
    // We set `includeSoftDeletes` to `true` to make this auth check idempotent
    await authorizeUserHasAccessToThread({ threadId: props.threadId, context, includeSoftDeletes: true });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  restoreThread
 * -------------------------------------------------------------------------------------------------
 */

type RestoreThreadProps = {
  threadId: string;
};

export const restoreThread = createAction<RestoreThreadProps>("thread.restoreThread", {
  operations: async (props) => {
    return ops.thread.restoreThread({
      threadId: props.threadId,
    });
  },
  authorize: async (props, context) => {
    await authorizeUserHasAccessToThread({ threadId: props.threadId, context, includeSoftDeletes: true });
  },
});

export const actions = {
  updateThreadSubject,
  addGroupToThread,
  removeGroupFromThread,
  applyLabelToThread,
  removeLabelFromThread,
  updateThreadVisibility,
  markThreadSeen,
  markThreadResolved,
  markThreadNotResolved,
  deleteThread,
  restoreThread,
};
